/* ATELIER BROCHURE READER - overlay flipbook for /pages/brochures
   ------------------------------------------------------------------
   Ported from _brochure-harness-assets/atelier-brochure-reader.js (the
   verified harness build, commit 9345d5f) - engine + chrome unchanged.
   The full "click the cover -> read the book" experience:
    - opens as a full-viewport overlay from a brochure card (FLIP zoom of the cover)
    - lazy-loads three.js (vendored theme asset) ONLY on first open
    - multi-leaf WebGL paper-curl engine (the Chris-approved baseline + riffle)
    - chrome: close/Esc/browser-back, prev/next, click-to-type page counter,
      scrubber with page preview bubble, all-pages grid, scroll-view toggle,
      fullscreen, arrow/PgUp/PgDn/Home/End keys, zoom+pan, drag to curl
    - hardened: texture residency window, render-on-demand + rAF watchdog,
      context-loss -> scroll reader, no-WebGL -> scroll reader
   Tune (Chris approved 2026-07-09): curl 2.0, focus 3.2, turn 1000ms,
   shade 0.5, riffle stagger 110ms.
   THEME WIRING (emitted by sections/atelier-brochures.liquid and
   assets/atelier-brochures-data.js):
    - window.ATBrochureAssets.three   vendored atelier-three.min.js asset URL
    - window.ATBrochureAssets.flick   page-flick.mp3 on Shopify Files
    - window.ATBrochurePageUrl(rec,n) page-image CDN URL builder
    - <template id="atbrLoaderTpl">   the animated BSD logo loader (veil)
    - rec.ar = per-book page aspect (w/h), default 0.70707 (A4 portrait).
      Spread-format PDFs were split at import; gb-flush-plates is a true
      landscape book (1.484), zm-toilets square-ish (0.985).
   Test modes: ?hud=1 residency pill, ?selftest=1#read=<id>,
   ?pose=1#read=<id> for screenshots. WebGL needs http (file:// = no). */
(function(){
'use strict';
if(window.AtelierBrochureReader) return;

var CFG=window.ATBrochureAssets||{};
var THREE_SRC=CFG.three||'https://cdn.jsdelivr.net/npm/three@0.150.1/build/three.min.js';
var H=2.0, W=H*0.70707, SEGS=64, MARGIN=0.14;
var curlMax=2.0, curlP=3.2, TURN=1000, shadeAmt=0.5;   /* approved tune */
var RIFFLE_MS=110, MAXF=6, FAST_MIN=320;

/* Styled to the REAL Atelier overlay language (see assets/atelier-lightbox.css +
   atelier-brands-page.css): dimmed translucent backdrop (the page shows through -
   an overlay, not a new screen), chrome = frosted floating CARDS (tint carries
   readability, blur is progressive enhancement), Sora UI + Outfit 300 headings,
   crimson accents, white/cream browsing surfaces. */
var CSS=[
'.at-brr{position:fixed;inset:0;z-index:1000;display:flex;flex-direction:column;',
'  font-family:"Sora",system-ui,-apple-system,sans-serif;color:#f2ede3;',
'  opacity:0;transition:opacity .28s ease}',
/* our display:flex beats the UA [hidden] rule, so hidden must be explicit -
   without this the closed overlay stays as an invisible full-screen click-eater */
'.at-brr[hidden]{display:none}',
'.at-brr.on{opacity:1}',
/* backdrop on its own layer so the close-flight can fade it under the flying canvas.
   display:block is LOAD-BEARING on the theme: base.css hides every empty styled
   <div> (div:empty{display:none}) and this div is intentionally empty - same
   documented workaround as the header scrim (atelier-header.css #atDscrim). */
'.at-brr .brr-bg{display:block;position:absolute;inset:0;',
'  background:radial-gradient(130% 100% at 50% 0%,rgba(32,28,23,.86) 0%,rgba(16,14,12,.94) 72%)}',
'.at-brr .brr-bg,.at-brr .brr-top,.at-brr .brr-bar{transition:opacity .5s ease}',
'.at-brr.flying .brr-bg,.at-brr.flying .brr-top,.at-brr.flying .brr-bar,',
'.at-brr.flying .brr-hint,.at-brr.flying .brr-hud{opacity:0}',
'.at-brr *,.at-brr *::before,.at-brr *::after{box-sizing:border-box}',
'.at-brr button{font:inherit}',
'.at-brr :focus-visible{outline:2px solid #c41230;outline-offset:3px;border-radius:4px}',
/* frosted floating card - the Atelier lightbox chrome recipe */
'.at-brr .brr-card{background:rgba(14,15,18,.30);border:1px solid rgba(255,255,255,.10);border-radius:16px;',
'  -webkit-backdrop-filter:blur(18px);backdrop-filter:blur(18px)}',
/* floating top chrome - no bar, no card: the book owns the height. One quiet
   identifying line top-left, the close circle top-right, both OVER the stage */
'.at-brr .brr-top{position:absolute;top:0;left:0;right:0;z-index:6;display:flex;align-items:flex-start;',
'  justify-content:space-between;gap:14px;padding:14px clamp(14px,2.5vw,26px);pointer-events:none}',
'.at-brr .brr-top .brr-x{pointer-events:auto}',
'.at-brr .brr-ey{margin:0 0 4px;font-size:10.5px;letter-spacing:.28em;text-transform:uppercase;',
'  color:#c41230;font-weight:600}',
'.at-brr .brr-id b{display:block;font-size:13.5px;font-weight:600;letter-spacing:.01em;',
'  color:rgba(255,255,255,.92);line-height:1.3}',
'.at-brr .brr-id span{display:block;margin-top:2px;font-family:"Outfit",system-ui,sans-serif;font-weight:300;',
'  font-size:12px;color:rgba(242,237,227,.55);letter-spacing:.02em}',
'.at-brr .brr-x{flex:0 0 auto;width:44px;height:44px;border-radius:50%;border:1px solid rgba(255,255,255,.14);',
'  background:rgba(255,255,255,.05);color:#f2ede3;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;',
'  transition:background .2s,border-color .2s}',
'.at-brr .brr-x:hover{background:rgba(255,255,255,.13);border-color:rgba(255,255,255,.3)}',
'.at-brr .brr-x svg{width:18px;height:18px}',
/* stage - the book floats on the dimmed backdrop, controls stay in the gutters */
'.at-brr .brr-stage{position:relative;flex:1;min-height:0}',
'.at-brr .brr-cv{position:absolute;inset:0;width:100%;height:100%;touch-action:none;cursor:grab;display:block}',
'.at-brr .brr-cv.grabbing{cursor:grabbing}',
'.at-brr.zoomed .brr-cv{cursor:move}',
'.at-brr.simple .brr-cv{display:none}',
'.at-brr .brr-scroll{display:none;position:absolute;inset:0;overflow-y:auto;padding:14px 12px 30px;-webkit-overflow-scrolling:touch}',
'.at-brr.simple .brr-scroll{display:block}',
'.at-brr .brr-scroll img{display:block;width:100%;max-width:720px;margin:0 auto 14px;box-shadow:0 10px 30px rgba(0,0,0,.4)}',
'.at-brr .brr-hint{position:absolute;left:50%;bottom:10px;transform:translateX(-50%);color:rgba(242,237,227,.5);',
'  font-family:"Outfit",system-ui,sans-serif;font-weight:300;font-size:12px;letter-spacing:.04em;pointer-events:none;',
'  transition:opacity .5s;white-space:nowrap;max-width:94%;overflow:hidden;text-overflow:ellipsis}',
'.at-brr.touched .brr-hint{opacity:0}',
'.at-brr .brr-banner{display:none;position:absolute;top:0;left:0;right:0;background:rgba(58,51,42,.95);color:#f0e8da;',
'  font-size:12.5px;padding:10px 14px;text-align:center;z-index:4;border-radius:0 0 12px 12px}',
'.at-brr .brr-banner.on{display:block}',
/* loading veil - the locked animated BSD logo loader (Hex Wave + Pop, see
   snippets/atelier-logo-loader.liquid via #atbrLoaderTpl; text dots = fallback).
   The tile animation only runs while the veil is shown (.on) - it is a
   main-thread SVG animation, never leave it running invisibly. */
'.at-brr .brr-load{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;pointer-events:none;',
'  font-family:"Outfit",system-ui,sans-serif;font-weight:300;font-size:13px;letter-spacing:.16em;text-transform:uppercase;',
'  color:rgba(242,237,227,.55);opacity:0;transition:opacity .3s}',
'.at-brr .brr-load.on{opacity:1}',
'.at-brr .brr-load i{font-style:normal;animation:atbrrp 1.2s ease-in-out infinite}',
'.at-brr .brr-load i:nth-child(2){animation-delay:.15s}',
'.at-brr .brr-load i:nth-child(3){animation-delay:.3s}',
'@keyframes atbrrp{0%,100%{opacity:.3}50%{opacity:1}}',
'.at-brr .brr-load svg{width:112px;height:auto;display:block;overflow:visible}',
'.at-brr .brr-load.on .bath>*{transform-box:fill-box;transform-origin:50% 50%;',
'  animation:atbrr-hw 1.9s ease-in-out infinite both}',
'@keyframes atbrr-hw{0%{opacity:1;transform:scale(1)}26%{opacity:.25;transform:scale(.86)}',
'  52%{opacity:1;transform:scale(1)}100%{opacity:1;transform:scale(1)}}',
/* hud (dev, ?hud=1) */
'.at-brr .brr-hud{position:absolute;top:10px;right:12px;z-index:5;display:none;gap:8px;align-items:center;',
'  background:rgba(0,0,0,.42);color:#e9e3d8;font-size:11px;letter-spacing:.04em;padding:7px 12px;border-radius:999px;',
'  font-variant-numeric:tabular-nums;pointer-events:none}',
'.at-brr .brr-hud.on{display:flex}',
'.at-brr .brr-hud b{color:#fff;font-weight:600}',
/* bottom card */
'.at-brr .brr-bar{display:flex;align-items:center;justify-content:center;gap:8px;flex-wrap:wrap;',
'  align-self:center;margin:0 0 14px;padding:8px 12px;flex:0 0 auto;',
'  width:min(980px,calc(100% - 2*clamp(14px,2.5vw,26px)))}',
'.at-brr .brr-ib{border:1px solid rgba(255,255,255,.12);background:rgba(255,255,255,.05);border-radius:12px;',
'  width:42px;height:42px;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;color:#f2ede3;',
'  flex:0 0 auto;transition:background .2s,border-color .2s,color .2s}',
'.at-brr .brr-ib:hover{background:rgba(255,255,255,.13)}',
'.at-brr .brr-ib:disabled{opacity:.3;cursor:default;background:rgba(255,255,255,.05)}',
'.at-brr .brr-ib[aria-pressed="true"]{background:#f2ede3;color:#1f2125;border-color:#f2ede3}',
'.at-brr .brr-ib svg{width:19px;height:19px}',
'.at-brr .brr-count{min-width:100px;text-align:center;font-size:13px;font-variant-numeric:tabular-nums;',
'  color:rgba(242,237,227,.62);cursor:pointer;border-radius:10px;padding:6px 4px}',
'.at-brr .brr-count:hover{background:rgba(255,255,255,.07)}',
'.at-brr .brr-count b{color:#fff;font-weight:600}',
'.at-brr .brr-count input{width:70px;height:32px;border:1px solid rgba(255,255,255,.3);border-radius:8px;',
'  text-align:center;font-size:13px;font-family:inherit;background:#fff;color:#1f2125}',
'.at-brr .brr-zchip{display:none;align-items:center;justify-content:center;',
'  border:1px solid rgba(255,255,255,.12);background:rgba(255,255,255,.05);',
'  border-radius:999px;height:42px;padding:0 13px;font-size:12px;font-weight:600;color:#f2ede3;cursor:pointer;',
'  font-variant-numeric:tabular-nums;flex:0 0 auto;line-height:1}',
'.at-brr .brr-zchip.on{display:inline-flex}',
'.at-brr .brr-zchip:hover{background:rgba(255,255,255,.13)}',
/* scrubber */
'.at-brr .brr-scrub{position:relative;flex:1 1 170px;min-width:120px;max-width:420px;height:42px;',
'  display:flex;align-items:center;cursor:pointer;touch-action:none;padding:0 10px}',
/* .fl and .hd are empty <div>s - explicit display beats the theme-wide
   div:empty{display:none} rule (see .brr-bg note above) */
'.at-brr .brr-scrub .tr{position:relative;width:100%;height:4px;border-radius:2px;background:rgba(255,255,255,.16)}',
'.at-brr .brr-scrub .fl{display:block;position:absolute;left:0;top:0;bottom:0;background:#c41230;border-radius:2px}',
'.at-brr .brr-scrub .hd{display:block;position:absolute;top:50%;left:0;width:14px;height:14px;border-radius:50%;background:#fff;',
'  border:2px solid #c41230;transform:translate(-50%,-50%);box-shadow:0 1px 4px rgba(0,0,0,.4);transition:transform .12s}',
'.at-brr .brr-scrub:hover .hd{transform:translate(-50%,-50%) scale(1.15)}',
'.at-brr .brr-scrub .bub{position:absolute;bottom:40px;left:0;transform:translateX(-50%);background:#fff;color:#1f2125;',
'  border-radius:10px;padding:6px 8px;display:none;flex-direction:column;align-items:center;gap:4px;pointer-events:none;',
'  white-space:nowrap;font-size:11px;font-weight:600;box-shadow:0 1px 2px rgba(31,33,37,.04),0 14px 32px -20px rgba(31,33,37,.6);',
'  z-index:6;font-variant-numeric:tabular-nums}',
'.at-brr .brr-scrub.live .bub{display:flex}',
'.at-brr .brr-scrub .bub img{width:56px;height:79px;object-fit:cover;border-radius:4px;background:#f4f5f6;display:block}',
/* all-pages grid - cream Atelier browsing surface, white cards */
'.at-brr .brr-grid{position:absolute;inset:0;z-index:8;display:flex;flex-direction:column;',
'  background:radial-gradient(125% 72% at 50% -8%,#fff 0%,#f8f9fa 55%);color:#2c2e32;',
'  opacity:0;transition:opacity .22s ease;pointer-events:none}',
'.at-brr .brr-grid.on{opacity:1;pointer-events:auto}',
'.at-brr .brr-grid-hd{display:flex;align-items:center;justify-content:space-between;gap:14px;',
'  padding:20px clamp(18px,4vw,48px) 12px;flex:0 0 auto}',
'.at-brr .brr-grid-hd .brr-ey{color:#c41230}',
'.at-brr .brr-grid-hd b{display:block;font-family:"Outfit",system-ui,sans-serif;font-weight:300;',
'  font-size:clamp(20px,3vw,30px);letter-spacing:-.015em;color:#1f2125;line-height:1.05}',
'.at-brr .brr-gx{flex:0 0 auto;width:44px;height:44px;border-radius:50%;border:1px solid rgba(29,31,35,.16);',
'  background:#fff;color:#1f2125;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;',
'  transition:background .2s}',
'.at-brr .brr-gx:hover{background:#f4f5f6}',
'.at-brr .brr-gx svg{width:18px;height:18px}',
'.at-brr .brr-grid-sc{flex:1;overflow-y:auto;padding:6px clamp(18px,4vw,48px) 34px}',
'.at-brr .brr-grid-in{display:grid;grid-template-columns:repeat(auto-fill,minmax(128px,1fr));gap:16px}',
'.at-brr .brr-gth{background:#fff;border:1px solid rgba(29,31,35,.1);border-radius:12px;overflow:hidden;',
'  padding:0;cursor:pointer;color:inherit;text-align:center;transform:translate3d(0,0,0);',
'  box-shadow:0 1px 2px rgba(31,33,37,.04),0 14px 32px -20px rgba(31,33,37,.22);',
'  transition:transform .3s ease,box-shadow .3s ease,border-color .3s ease}',
'.at-brr .brr-gth:hover{transform:translate3d(0,-4px,0);border-color:rgba(29,31,35,.16);',
'  box-shadow:0 2px 4px rgba(31,33,37,.05),0 28px 48px -24px rgba(31,33,37,.3)}',
'.at-brr .brr-gth.cur{outline:2px solid #c41230;outline-offset:2px}',
'.at-brr .brr-gth img{width:100%;aspect-ratio:1/1.4142;object-fit:cover;display:block;background:#f4f5f6}',
'.at-brr .brr-gth span{display:block;padding:7px 4px 8px;font-size:11px;font-weight:600;color:#565a60;',
'  font-variant-numeric:tabular-nums}',
/* cover FLIP clone */
'.brr-flip{position:fixed;z-index:1002;object-fit:cover;border-radius:10px;pointer-events:none;',
'  box-shadow:0 26px 60px -18px rgba(0,0,0,.55)}',
/* while the entrance or close animation runs, the reader takes no input */
'.at-brr.closing .brr-stage,.at-brr.closing .brr-bar,.at-brr.closing .brr-top,',
'.at-brr.opening .brr-stage,.at-brr.opening .brr-bar{pointer-events:none}',
/* small screens - canonical 767 boundary (atelier-mobile-standard) */
'@media (max-width:767px){',
'  .at-brr .brr-top{padding:10px 14px}',
/* width-limited phone pages run tall - keep the canvas below the title line
   so the identifying text never sits on the paper */
'  .at-brr .brr-stage{margin-top:46px}',
/* long titles must never collide with the close circle on a 360 screen */
'  .at-brr .brr-id{min-width:0;overflow:hidden}',
'  .at-brr .brr-id b{font-size:12.5px}',
'  .at-brr .brr-id b,.at-brr .brr-id span{white-space:nowrap;overflow:hidden;text-overflow:ellipsis}',
'  .at-brr .brr-bar{gap:6px;margin-bottom:10px;padding:8px 10px}',
/* 44px tap floor (atelier-mobile-standard) */
'  .at-brr .brr-ib{width:44px;height:44px}',
'  .at-brr .brr-zchip{height:44px}',
'  .at-brr .brr-scrub{flex-basis:100%;order:10;max-width:none;min-height:44px}',
'  .at-brr .brr-count{min-width:84px}',
'  .at-brr .brr-count input{font-size:16px}',   /* <16px = iOS focus-zoom */
'  .at-brr .brr-grid-in{grid-template-columns:repeat(auto-fill,minmax(96px,1fr));gap:11px}',
'}',
/* touch: no stuck hover (theme rule) */
'@media (hover:none){',
'  .at-brr .brr-gth:hover{transform:translate3d(0,0,0);border-color:rgba(29,31,35,.1);',
'    box-shadow:0 1px 2px rgba(31,33,37,.04),0 14px 32px -20px rgba(31,33,37,.22)}',
'  .at-brr .brr-ib:hover{background:rgba(255,255,255,.05)}',
'}',
'@media (prefers-reduced-motion:reduce){',
'  .at-brr{transition:none}',
'  .at-brr .brr-grid{transition:none}',
'  .brr-flip{transition:none!important}',
'  .at-brr .brr-load.on .bath>*{animation:none}',
'}'
].join('\n');

var ICONS={
  x:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>',
  prev:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>',
  next:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>',
  grid:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3.5" y="3.5" width="7" height="7" rx="1"/><rect x="13.5" y="3.5" width="7" height="7" rx="1"/><rect x="3.5" y="13.5" width="7" height="7" rx="1"/><rect x="13.5" y="13.5" width="7" height="7" rx="1"/></svg>',
  scroll:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 6h16M4 12h16M4 18h10"/></svg>',
  fs:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3"/><path d="M16 3h3a2 2 0 0 1 2 2v3"/><path d="M8 21H5a2 2 0 0 1-2-2v-3"/><path d="M16 21h3a2 2 0 0 0 2-2v-3"/></svg>',
  zin:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/><path d="M8 11h6M11 8v6"/></svg>',
  zout:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/><path d="M8 11h6"/></svg>',
  snd:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5 6 9H3v6h3l5 4z"/><path d="M15.5 8.5a5 5 0 0 1 0 7"/><path d="M18.4 5.6a9 9 0 0 1 0 12.8"/></svg>',
  sndOff:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5 6 9H3v6h3l5 4z"/><path d="M22 9l-6 6"/><path d="M16 9l6 6"/></svg>'
};

/* ---------------- module state ---------------- */
var root=null, els={}, built=false;
var openState=false, REC=null, gen=0;
var N=0, P=[], V=[], SPREADS=1;
var single=false;   /* single-page portrait mode - see buildV() */
var simpleMode=false, lost=false, glReady=false;
var renderer=null, scene=null, camera=null, loaderTex=null, maxAniso=1, BLANK=null, BRAND_BLANK=null;
var pgLeft=null, pgRight=null, POOL=[];
var CACHE={}, pendingTex={}, PAGE_MB=(1024*1448*4)/1048576;
var sBase=0, flights=[], jumpLeaf=null, jumpPending=null;
var pendNext=0, pendPrev=0, lastLaunch=-1e9, pumpTimer=0;
var tickId=0, tickTimer=0, lastTick=0, rafId=0, drawing=false;
var dragging=false, dragL=null, moved=0, startX=0, downT=0, touchPend=null;
var scrubbing=false, lastScrub=0, countEditing=false;
var gridBuilt=false;
var flipImg=null, flipT0=0, veilT=0, coverHold=false;
var closing=false, closeTimer=0, openerCover=null;
var silentTurns=0;   /* pump-launched turns that belong to ONE user action = ONE sound */
var viewX=0;   /* camera x: centers single-page spreads, driven by page progress */
var zoom=1, panX=0, panY=0;                 /* reading zoom + pan */
var panDrag=null, ptrs={}, pinch0=null;     /* pan-drag + pinch state */
var startPageReq=0;                          /* deep-linked page (#read=id&p=N) */
/* page-turn sound: plays ONLY from a real recording ("Page turned" by Beussa,
   freesound 749880, CC0 - synthesised paper was rejected). Hosted on Shopify
   Files (CDN + CORS fine). No file = no button. OFF by default; the toggle
   remembers an explicit opt-in. */
var SND_SRC=CFG.flick||'';
var soundOn=(function(){ try{ return localStorage.getItem('atbrr-snd')==='1'; }catch(e){ return false; } })();
var AC=null, sndBuf=null, sndTried=false;
var hudOn=/[?&]hud=1/.test(location.search);
var selftestRan=false, poseRan=false;

function pad2(n){ return String(n).padStart(2,'0'); }
/* page images live on Shopify Files; the generated data asset owns the URL
   pattern (window.ATBrochurePageUrl). The harness-relative path stays as the
   last-ditch fallback for local dev. */
function defaultPageUrl(rec,n){
  if(window.ATBrochurePageUrl) return window.ATBrochurePageUrl(rec,n);
  return '_brochure-harness-assets/pages/'+rec.id+'/page-'+pad2(n)+'.webp';
}
var pageUrl=defaultPageUrl;

/* ---------------- DOM ---------------- */
function injectCSS(){
  if(document.getElementById('at-brr-css')) return;
  var s=document.createElement('style'); s.id='at-brr-css'; s.textContent=CSS;
  document.head.appendChild(s);
}
function buildDOM(){
  if(built) return; built=true;
  root=document.createElement('div');
  root.className='at-brr'; root.hidden=true;
  root.setAttribute('role','dialog'); root.setAttribute('aria-modal','true'); root.setAttribute('aria-label','Brochure reader');
  root.innerHTML=
    '<div class="brr-bg" data-brr="bg"></div>'
    +'<header class="brr-top">'
    +  '<div class="brr-id">'
    +    '<b data-brr="title"></b>'
    +    '<span data-brr="meta"></span>'
    +  '</div>'
    +  '<button class="brr-x" data-brr="close" aria-label="Close the reader">'+ICONS.x+'</button>'
    +'</header>'
    +'<div class="brr-stage" data-brr="stage">'
    +  '<canvas class="brr-cv" data-brr="cv"></canvas>'
    +  '<div class="brr-scroll" data-brr="scroll"></div>'
    +  '<div class="brr-load" data-brr="load">Preparing the pages<i>.</i><i>.</i><i>.</i></div>'
    +  '<div class="brr-banner" data-brr="banner"></div>'
    +  '<div class="brr-hud" data-brr="hud"></div>'
    +  '<div class="brr-hint" data-brr="hint">Click to turn &middot; hold and drag to curl &middot; scroll to zoom</div>'
    +'</div>'
    +'<footer class="brr-bar brr-card">'
    +  '<button class="brr-ib" data-brr="prev" aria-label="Previous page">'+ICONS.prev+'</button>'
    +  '<span class="brr-count" data-brr="count" title="Click to type a page number"></span>'
    +  '<button class="brr-ib" data-brr="next" aria-label="Next page">'+ICONS.next+'</button>'
    +  '<div class="brr-scrub" data-brr="scrub">'
    +    '<div class="tr"><div class="fl" data-brr="sfill"></div><div class="hd" data-brr="shandle"></div></div>'
    +    '<div class="bub" data-brr="sbub"><img data-brr="simg" alt=""><span data-brr="slabel"></span></div>'
    +  '</div>'
    +  '<button class="brr-ib" data-brr="zoutBtn" aria-label="Zoom out" title="Zoom out">'+ICONS.zout+'</button>'
    +  '<button class="brr-zchip" data-brr="zchip" title="Reset zoom"></button>'
    +  '<button class="brr-ib" data-brr="zinBtn" aria-label="Zoom in" title="Zoom in">'+ICONS.zin+'</button>'
    +  '<button class="brr-ib" data-brr="gridBtn" aria-label="All pages" aria-pressed="false" title="All pages">'+ICONS.grid+'</button>'
    +  '<button class="brr-ib" data-brr="sndBtn" aria-label="Page-turn sound" title="Page-turn sound">'+(soundOn?ICONS.snd:ICONS.sndOff)+'</button>'
    +  '<button class="brr-ib" data-brr="scrollBtn" aria-label="Scroll view" aria-pressed="false" title="Scroll view">'+ICONS.scroll+'</button>'
    +  '<button class="brr-ib" data-brr="fsBtn" aria-label="Fullscreen" title="Fullscreen">'+ICONS.fs+'</button>'
    +'</footer>'
    +'<div class="brr-grid" data-brr="grid">'
    +  '<div class="brr-grid-hd">'
    +    '<div><p class="brr-ey">All pages</p><b data-brr="gtitle"></b></div>'
    +    '<button class="brr-gx" data-brr="gclose" aria-label="Back to the book">'+ICONS.x+'</button>'
    +  '</div>'
    +  '<div class="brr-grid-sc"><div class="brr-grid-in" data-brr="gwrap"></div></div>'
    +'</div>';
  document.body.appendChild(root);
  root.querySelectorAll('[data-brr]').forEach(function(el){ els[el.getAttribute('data-brr')]=el; });
  els.sndBtn.style.display='none';            /* appears only once a real recording loads */
  if(hudOn) els.hud.classList.add('on');
  /* touch wording + no fullscreen API on iPhone Safari */
  try{ if(matchMedia('(hover:none)').matches) els.hint.textContent='Tap to turn · drag to curl · pinch to zoom'; }catch(e){}
  if(!document.documentElement.requestFullscreen) els.fsBtn.style.display='none';
  /* the veil = the animated BSD logo (template rendered by the section);
     the text dots stay as the no-template fallback */
  var tpl=document.getElementById('atbrLoaderTpl');
  if(tpl&&tpl.content&&tpl.content.firstElementChild){
    els.load.innerHTML='';
    els.load.appendChild(tpl.content.firstElementChild.cloneNode(true));
  }
  bindUI();
}
/* per-tile animation-delay by x-centroid (the Hex Wave sweep, span 0.65s).
   getBBox needs a rendered SVG, so this runs lazily on the first visible veil. */
var loaderStaggered=false;
function staggerLoader(){
  if(loaderStaggered) return;
  var svg=els.load&&els.load.querySelector('svg'); if(!svg) return;
  var tiles=[].slice.call(svg.querySelectorAll('.bath > *')); if(!tiles.length) return;
  var xs=tiles.map(function(t){ try{ var b=t.getBBox(); return b.x+b.width/2; }catch(e){ return 0; } });
  var min=Math.min.apply(null,xs), max=Math.max.apply(null,xs), r=(max-min)||1;
  tiles.forEach(function(t,i){ t.style.animationDelay=(((xs[i]-min)/r)*0.65).toFixed(3)+'s'; });
  loaderStaggered=true;
}

/* ---------------- three.js lazy load ---------------- */
function ensureThree(ok,fail){
  if(window.THREE) return ok();
  var s=document.createElement('script');
  s.src=THREE_SRC;
  s.onload=function(){ ok(); };
  s.onerror=function(){ fail('three.js failed to load - using the scroll view.'); };
  document.head.appendChild(s);
}

/* ---------------- fallback ---------------- */
function showFallback(msg){
  simpleMode=true; root.classList.add('simple');
  els.scrollBtn.setAttribute('aria-pressed','true');
  veil(false); coverHold=false; dropFlip(true);
  if(msg){ els.banner.textContent=msg; els.banner.classList.add('on'); }
}
function showGL(){
  if(lost||!glReady) return;
  simpleMode=false; root.classList.remove('simple');
  els.scrollBtn.setAttribute('aria-pressed','false');
  els.banner.classList.remove('on');
  resize(); requestRender();
}
function veil(on){ if(on) staggerLoader(); els.load.classList.toggle('on',!!on); }

/* ---------------- textures ---------------- */
/* a face that is KNOWN blank (null src - sheet backs and the turned pile in
   single mode) carries the supplier mark so it reads intentional, not like a
   page that failed to load; a real page still fetching shows plain paper */
function texFor(src){
  if(src&&CACHE[src]) return CACHE[src];
  if(!src) return BRAND_BLANK||BLANK;
  return BLANK;
}
/* paper + the supplier logo, centred - built per book from window.ATBrandLogos
   (theme asset URLs; absent in the harness -> plain paper fallback) */
function buildBrandBlank(rec){
  if(BRAND_BLANK){ BRAND_BLANK.dispose(); BRAND_BLANK=null; }
  var src=(window.ATBrandLogos||{})[rec.brand];
  if(!src) return;
  var myGen=gen, img=new Image();
  img.crossOrigin='anonymous';
  img.onload=function(){
    if(myGen!==gen||!openState||!glReady) return;
    try{
      var ar=rec.ar||0.70707, ch=1024, cw=Math.round(ch*ar);
      var c=document.createElement('canvas'); c.width=cw; c.height=ch;
      var x=c.getContext('2d');
      x.fillStyle='#efece6'; x.fillRect(0,0,cw,ch);
      var s=Math.min((cw*0.42)/img.width,(ch*0.16)/img.height);
      var w=img.width*s, h=img.height*s;
      x.globalAlpha=0.85;
      x.drawImage(img,(cw-w)/2,(ch-h)/2,w,h);
      BRAND_BLANK=new THREE.CanvasTexture(c);
      BRAND_BLANK.minFilter=THREE.LinearFilter; BRAND_BLANK.generateMipmaps=false; BRAND_BLANK.anisotropy=maxAniso;
      refreshMaps(); requestRender();
    }catch(e){}
  };
  img.src=src;
}
function t1count(){ var c=0; for(var i=0;i<flights.length;i++) if(flights[i].target===1)c++; return c; }
function destSpread(){
  if(jumpLeaf) return jumpLeaf.destS;
  if(jumpPending!=null) return jumpPending;
  return sBase+t1count();
}
function addWin(sp,out){
  for(var i=2*sp-2;i<=2*sp+3;i++){ if(i>=0&&i<V.length&&V[i]&&out.indexOf(V[i])<0) out.push(V[i]); }
}
/* the two static stack pages - derived ONE way, used by refreshMaps AND the
   residency window so the visible stack can never have its texture evicted
   (multi-reversal used to push pgRight outside the dest window = paper flash) */
function stackSrcs(){
  if(jumpLeaf){
    if(jumpLeaf.destS>jumpLeaf.fromS) return {l:V[2*jumpLeaf.fromS], r:V[2*jumpLeaf.destS+1]};
    return {l:V[2*jumpLeaf.destS], r:V[2*jumpLeaf.fromS+1]};
  }
  return {l:V[2*sBase], r:V[2*(sBase+flights.length)+1]};
}
function neededSrcs(){
  var out=[], st=stackSrcs();
  if(st.l) out.push(st.l);
  if(st.r&&out.indexOf(st.r)<0) out.push(st.r);
  addWin(destSpread(),out); addWin(sBase,out);
  var all=jumpLeaf?flights.concat([jumpLeaf]):flights;
  for(var i=0;i<all.length;i++){
    var L=all[i];
    if(L.front&&out.indexOf(L.front)<0) out.push(L.front);
    if(L.back&&out.indexOf(L.back)<0) out.push(L.back);
  }
  return out;
}
function syncTextures(){
  if(!glReady) return;
  var keep=neededSrcs(), myGen=gen;
  Object.keys(CACHE).forEach(function(src){
    if(keep.indexOf(src)<0){ CACHE[src].dispose(); delete CACHE[src]; }
  });
  keep.forEach(function(src){
    if(CACHE[src]||pendingTex[src]) return;
    pendingTex[src]=1;
    loaderTex.load(src,function(tx){
      delete pendingTex[src];
      if(!openState||myGen!==gen){ tx.dispose(); return; }
      tx.minFilter=THREE.LinearFilter; tx.generateMipmaps=false; tx.anisotropy=maxAniso;
      CACHE[src]=tx; refreshMaps(); updateHUD(); requestRender(); maybeReady();
    },undefined,function(){ delete pendingTex[src]; });
  });
  updateHUD();
}
function residentCount(){ return Object.keys(CACHE).length; }
function updateHUD(){
  if(!hudOn||!els.hud) return;
  var mb=(residentCount()*PAGE_MB).toFixed(0);
  els.hud.innerHTML='resident <b>'+residentCount()+'</b> &middot; ~<b>'+mb+' MB</b> &middot; sheets <b>'
    +(flights.length+(jumpLeaf?1:0))+'</b> &middot; '+(drawing?'drawing':'idle');
}
/* first VISIBLE texture arrived -> lift the veil, retire the FLIP clone.
   Checks the static stack pages (what is actually on screen at rest), NOT
   page 1: a deep-link open (#read=<id>&p=7) never loads page 1 - it is
   outside the residency window - so a page-1 check left the veil on forever. */
function maybeReady(){
  var st=stackSrcs();
  if((st.l&&CACHE[st.l])||(st.r&&CACHE[st.r])){ veil(false); dropFlip(); }
}

/* ---------------- GL init ---------------- */
function initGL(){
  if(glReady) return true;
  try{ renderer=new THREE.WebGLRenderer({canvas:els.cv,antialias:true,alpha:true,powerPreference:'low-power'}); }
  catch(e){ showFallback('WebGL unavailable on this device - using the scroll view.'); return false; }
  renderer.setPixelRatio(Math.min(window.devicePixelRatio||1, innerWidth<820?1.75:2));
  if(THREE.ColorManagement) THREE.ColorManagement.enabled=false;
  scene=new THREE.Scene(); camera=new THREE.PerspectiveCamera(30,1,0.1,100);
  var bc=document.createElement('canvas'); bc.width=bc.height=4;
  var bx=bc.getContext('2d'); bx.fillStyle='#efece6'; bx.fillRect(0,0,4,4);
  BLANK=new THREE.CanvasTexture(bc);
  loaderTex=new THREE.TextureLoader(); loaderTex.setCrossOrigin('anonymous');
  maxAniso=renderer.capabilities.getMaxAnisotropy();
  pgLeft=plane(); pgRight=plane();
  pgLeft.position.x=-W/2; pgRight.position.x=W/2;
  appliedW=W;
  els.cv.addEventListener('webglcontextlost',function(e){
    e.preventDefault(); lost=true;
    sBase=destSpread(); clearFlights();
    showFallback('The 3D view hit a snag - switched to the scroll view. Nothing lost.');
  },false);
  els.cv.addEventListener('webglcontextrestored',function(){
    lost=false;
    Object.keys(CACHE).forEach(function(k){ CACHE[k].needsUpdate=true; });
    showGL(); refreshMaps(); updateCount();
  },false);
  glReady=true;
  return true;
}
function plane(){
  var m=new THREE.Mesh(new THREE.PlaneGeometry(W,H), new THREE.MeshBasicMaterial({map:BLANK}));
  m.position.z=-0.012; scene.add(m); return m;
}
/* per-book page aspect: W follows the OPEN book (the spread-split import gives
   gb-flush-plates landscape pages, zm-toilets square-ish ones). Everything
   downstream - leaf strips, camera fit, view centering, cover FLIP math -
   reads W at call time; only the two static stack planes bake it into
   geometry, so they rebuild when the aspect changes. Must run before any
   coverRectPx() measurement (apiOpen measures for the entrance FLIP). */
var appliedW=0;
function setAspect(a){
  W=H*(a||0.70707);
  if(glReady&&appliedW!==W){
    pgLeft.geometry.dispose(); pgLeft.geometry=new THREE.PlaneGeometry(W,H);
    pgRight.geometry.dispose(); pgRight.geometry=new THREE.PlaneGeometry(W,H);
    pgLeft.position.x=-W/2; pgRight.position.x=W/2;
    appliedW=W;
  }
}
var LEAF_VS='varying vec2 vUv; varying vec3 vN;\nvoid main(){ vUv=uv; vN=normalize(normalMatrix*normal); gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0);}';
var LEAF_FS='uniform sampler2D uFront; uniform sampler2D uBack; uniform float uShade;\nvarying vec2 vUv; varying vec3 vN;\nvoid main(){ vec4 c = gl_FrontFacing ? texture2D(uFront,vUv) : texture2D(uBack, vec2(1.0-vUv.x,vUv.y)); float d=abs(normalize(vN).z); float sh=(1.0-uShade)+uShade*pow(d,0.75); gl_FragColor=vec4(c.rgb*sh,1.0);}';
function buildGeo(){
  var g=new THREE.BufferGeometry();
  var count=(SEGS+1)*2, pos=new Float32Array(count*3), nor=new Float32Array(count*3), uv=new Float32Array(count*2), idx=[];
  for(var i=0;i<=SEGS;i++){ for(var j=0;j<2;j++){ var vi=i*2+j; uv[vi*2]=i/SEGS; uv[vi*2+1]=j===0?1:0; } }
  for(i=0;i<SEGS;i++){ var a=i*2,b=i*2+1,c=(i+1)*2,d=(i+1)*2+1; idx.push(a,b,c, b,d,c); }
  g.setAttribute('position',new THREE.BufferAttribute(pos,3));
  g.setAttribute('normal',new THREE.BufferAttribute(nor,3));
  g.setAttribute('uv',new THREE.BufferAttribute(uv,2));
  g.setIndex(idx);
  return g;
}
function makeLeaf(){
  var g=buildGeo();
  var m=new THREE.ShaderMaterial({side:THREE.DoubleSide,
    uniforms:{uFront:{value:BLANK},uBack:{value:BLANK},uShade:{value:shadeAmt}},
    vertexShader:LEAF_VS,fragmentShader:LEAF_FS});
  var mesh=new THREE.Mesh(g,m); mesh.visible=false; scene.add(mesh);
  return {mesh:mesh,geo:g,mat:m,active:false,t:0,from:0,target:1,t0:0,dur:TURN,done:false,
          dragging:false,isJump:false,index:0,front:null,back:null,fromS:0,destS:0,dragD:1};
}
function acquire(){
  for(var i=0;i<POOL.length;i++){ if(!POOL[i].active){ var L=POOL[i]; L.active=true; L.done=false; L.dragging=false; L.isJump=false; L.kEff=null; return L; } }
  var nl=makeLeaf(); nl.active=true; POOL.push(nl); return nl;
}
function release(L){ L.active=false; L.dragging=false; L.isJump=false; L.mesh.visible=false; }
function clearFlights(){
  flights.slice().forEach(release); flights=[];
  if(jumpLeaf){ release(jumpLeaf); jumpLeaf=null; }
  jumpPending=null; pendNext=pendPrev=0; silentTurns=0;
  if(pumpTimer){ clearTimeout(pumpTimer); pumpTimer=0; }
}

function updateLeafGeo(L){
  var t=L.t, A=t*Math.PI, ds=W/SEGS, X=0, Z=0;
  var k=(L.kEff!=null)?L.kEff:curlMax*Math.sin(t*Math.PI);
  var pos=L.geo.attributes.position.array, nor=L.geo.attributes.normal.array;
  for(var i=0;i<=SEGS;i++){
    if(i>0){ var um=(i-0.5)/SEGS, thm=A+k*Math.pow(um,curlP); X+=ds*Math.cos(thm); Z+=ds*Math.sin(thm); }
    var u=i/SEGS, th=A+k*Math.pow(u,curlP), nx=-Math.sin(th), nz=Math.cos(th);
    for(var j=0;j<2;j++){ var vi=i*2+j, y=(j===0?H/2:-H/2);
      pos[vi*3]=X; pos[vi*3+1]=y; pos[vi*3+2]=Z; nor[vi*3]=nx; nor[vi*3+1]=0; nor[vi*3+2]=nz; }
  }
  L.geo.attributes.position.needsUpdate=true; L.geo.attributes.normal.needsUpdate=true;
  /* stack-order bias: must stay ABOVE the depth-buffer's resolvable step at the
     book's distance or coplanar flat sheets z-fight (= pages shimmering between
     each other while riffling). 0.0015 + the tightened near/far in resize() */
  var kk=L.isJump?0.6:(L.index-sBase);
  L.mesh.position.z=0.0015*kk*(2*t-1);
}
/* single mode, final page: the landing flip CONTINUES around the spine - the
   pile rotates past flat-left, swings down behind the spine and comes to rest
   FACE-DOWN underneath the back cover (a flip-chart page wrapping behind the
   pad), then hides once fully covered. Pivot = the spine (x=0): rotation.y
   -phi with the centre riding the arc keeps the spine edge fixed. Any launch
   away from the last page restores it instantly (untuck). Timer-driven steps,
   not rAF, so it cannot stall half-way in a hidden tab. */
var tucked=false, tuckTimer=0, tuckStep=0;
function untuck(){
  if(tuckTimer){ clearTimeout(tuckTimer); tuckTimer=0; }
  if(tuckStep){ clearTimeout(tuckStep); tuckStep=0; }
  tucked=false;
  if(glReady&&pgLeft){ pgLeft.rotation.y=0; pgLeft.position.set(-W/2,0,-0.012); }
}
function startTuck(){
  if(!glReady||tucked||tuckTimer||tuckStep||SPREADS<2) return;
  tuckTimer=setTimeout(function(){
    tuckTimer=0;
    if(!openState||!single||anyActive()||destSpread()!==SPREADS-1) return;
    /* deliberately slower than a page turn - at 420ms the wrap still read as
       the page disappearing (Chris); the half-revolution wants its own beat */
    var t0=performance.now(), dur=750, z0=-0.02;
    (function step(){
      tuckStep=0;
      var p=Math.min(1,(performance.now()-t0)/dur);
      var e=p<0.5?4*p*p*p:1-Math.pow(-2*p+2,3)/2;
      var ph=e*Math.PI;
      pgLeft.rotation.y=-ph;
      pgLeft.position.set(-(W/2)*Math.cos(ph),0,z0-(W/2)*Math.sin(ph));
      requestRender();
      if(p<1) tuckStep=setTimeout(step,16);
      else { tucked=true; refreshMaps(); requestRender(); }
    })();
  },80);
}
function refreshMaps(){
  if(!glReady) return;
  var st=stackSrcs(), leftS=st.l, rightS=st.r;
  pgLeft.material.map=texFor(leftS); pgRight.material.map=texFor(rightS);
  /* coverHold: while the card-cover FLIP clone is flying, the real cover stays
     hidden so there are never two covers on screen at once.
     single mode: the pile of TURNED pages stays on the left (branded blank,
     seamless with the landing sheet's back) instead of vanishing at landing;
     on the FINAL page the pile then TUCKS underneath the back cover (the book
     visibly closes - see startTuck) and stays hidden while tucked */
  pgLeft.visible=single?(sBase>0&&!tucked):!!leftS;
  pgRight.visible=!!rightS&&!coverHold;
  pgLeft.material.needsUpdate=pgRight.material.needsUpdate=true;
  var all=jumpLeaf?flights.concat([jumpLeaf]):flights;
  for(var i=0;i<all.length;i++){ var L=all[i];
    L.mat.uniforms.uFront.value=texFor(L.front); L.mat.uniforms.uBack.value=texFor(L.back); }
}

/* ---------------- counter / scrub / thumbs sync ---------------- */
function pagesLabel(sp){
  if(single) return String(sp+1);   /* one page per spread; V index != page no. */
  var l=2*sp, r=2*sp+1, hasL=!!V[l], hasR=!!V[r];
  if(hasL&&hasR) return l+'-'+r;
  if(hasR) return String(r);
  return String(l);
}
function updateCount(){
  var d=destSpread();
  if(!countEditing) els.count.innerHTML='<b>'+pagesLabel(d)+'</b> / '+N;
  els.prev.disabled=(d<=0); els.next.disabled=(d>=SPREADS-1);
  if(!scrubbing) setScrub(SPREADS>1?d/(SPREADS-1):0);
  highlightGrid(d);
}

/* ---------------- render on demand + watchdog ticker ---------------- */
function render(){ if(lost||simpleMode||!openState||!glReady) return; renderer.render(scene,camera); }
function requestRender(){ if(rafId) return; rafId=requestAnimationFrame(function(){ rafId=0; render(); }); }
function setDrawing(v){ if(drawing!==v){ drawing=v; updateHUD(); } }
function anyActive(){ return flights.length>0||!!jumpLeaf; }
function ensureTick(){
  if(!tickId) tickId=requestAnimationFrame(tick);
  if(!tickTimer) tickTimer=setTimeout(watchdog,90);
}
function watchdog(){
  tickTimer=0;
  if(!anyActive()||!openState) return;
  if(performance.now()-lastTick>80){
    if(tickId){ cancelAnimationFrame(tickId); tickId=0; }
    tick();
  } else tickTimer=setTimeout(watchdog,90);
}
/* riffling sheets are PAPER PRESSED TOGETHER: a sheet may never overtake the
   sheet ahead of it (mixed turn speeds used to fly one page through another),
   and a follower may never out-curl the angular gap to its leader - the
   tip-crossing condition k_follow <= k_lead + PI*(t_lead - t_follow). Clamped
   sheets ride pressed behind the leader and ease down once it lands. */
var GAP_T=0.035, GAP_K=0.05;
function kNat(t){ return curlMax*Math.sin(t*Math.PI); }
function enforceNesting(){
  var prev=null;
  for(var i=0;i<flights.length;i++){
    var L=flights[i];
    if(prev){
      var cap=prev.t-GAP_T;
      if(L.t>cap){
        L.t=Math.max(0,cap);
        if(L.done&&!L.dragging){ L.done=false; retarget(L,L.target,240); }
      }
    }
    L.kEff=kNat(L.t);
    if(prev){
      var kCap=prev.kEff+(prev.t-L.t)*Math.PI-GAP_K;
      if(L.kEff>kCap) L.kEff=Math.max(0,kCap);
    }
    prev=L;
  }
  if(jumpLeaf) jumpLeaf.kEff=kNat(jumpLeaf.t);
}
function tick(){
  tickId=0; lastTick=performance.now();
  if(!openState) return;
  var now=lastTick;
  var all=jumpLeaf?flights.concat([jumpLeaf]):flights.slice();
  for(var i=0;i<all.length;i++){
    var L=all[i];
    if(!L.dragging&&!L.done){
      var p=Math.min(1,(now-L.t0)/L.dur), e=p<0.5?4*p*p*p:1-Math.pow(-2*p+2,3)/2;
      L.t=L.from+(L.target-L.from)*e;
      if(p>=1){ L.t=L.target; L.done=true; }
    }
  }
  enforceNesting();
  for(var j=0;j<all.length;j++) updateLeafGeo(all[j]);
  resolveLandings();
  applyViewX();
  render();
  if(anyActive()){
    setDrawing(true);
    tickId=requestAnimationFrame(tick);
    if(!tickTimer) tickTimer=setTimeout(watchdog,90);
  }
  else setDrawing(false);
}
function resolveLandings(){
  var acted=true;
  while(acted){
    acted=false;
    if(flights.length){
      var Lo=flights[0];
      if(Lo.done&&!Lo.dragging&&Lo.target===1&&Lo.index===sBase){
        flights.shift(); sBase++; release(Lo); onLand(); acted=true; continue;
      }
      var Ln=flights[flights.length-1];
      if(Ln.done&&!Ln.dragging&&Ln.target===0&&Ln.index===sBase+flights.length-1){
        flights.pop(); release(Ln); onLand(); acted=true; continue;
      }
    }
    if(jumpLeaf&&jumpLeaf.done){
      sBase=jumpLeaf.destS; release(jumpLeaf); jumpLeaf=null; onLand(); acted=true;
    }
  }
  if(!flights.length&&!jumpLeaf&&jumpPending!=null) launchJump(jumpPending);
}
function onLand(){
  syncTextures(); refreshMaps(); updateCount(); pump();
  if(!flights.length&&!jumpLeaf&&jumpPending==null&&!pendNext&&!pendPrev){
    if(single&&sBase===SPREADS-1) startTuck();   /* settled on the back cover */
    syncHash();
  }
}

/* ---------------- turns: queue, stagger, reversal ---------------- */
function riffleDur(){
  if(closing) return 300;                     /* the ride home is brisk */
  return (pendNext+pendPrev>0||flights.length>0)?Math.max(FAST_MIN,TURN*0.55):TURN;
}
function stagger(){ return closing?70:RIFFLE_MS; }
function retarget(L,target,durBase){
  L.target=target; L.from=L.t; L.t0=performance.now();
  L.dur=Math.max(180,durBase*Math.abs(target-L.t)); L.done=(L.t===target);
}
function afterTurnChange(){
  if(coverHold){ coverHold=false; dropFlip(true); }   /* interacting mid-open-zoom ends the zoom */
  untuck();                                           /* leaving the closed state re-opens the pile */
  syncTextures(); refreshMaps(); updateCount(); ensureTick(); setDrawing(true); root.classList.add('touched');
}
function doTurn(d,forDrag){
  if(lost||simpleMode||!glReady||jumpLeaf||jumpPending!=null) return null;
  var i,L;
  if(d>0){
    for(i=0;i<flights.length;i++){ if(flights[i].target===0&&!flights[i].dragging){ retarget(flights[i],1,riffleDur()); flick(0.7); afterTurnChange(); return 'flip'; } }
    if(destSpread()>=SPREADS-1){ pendNext=0; return null; }
    if(flights.length>=MAXF) return 'wait';
    L=acquire(); L.index=sBase+flights.length; L.front=V[2*L.index+1]; L.back=V[2*L.index+2];
    L.t=0; L.dragD=1; retarget(L,1,riffleDur()); L.dragging=!!forDrag;
    flights.push(L);
  } else {
    for(i=flights.length-1;i>=0;i--){ if(flights[i].target===1&&!flights[i].dragging){ retarget(flights[i],0,riffleDur()); flick(0.7); afterTurnChange(); return 'flip'; } }
    if(destSpread()<=0){ pendPrev=0; return null; }
    if(flights.length>=MAXF) return 'wait';
    sBase--;
    L=acquire(); L.index=sBase; L.front=V[2*L.index+1]; L.back=V[2*L.index+2];
    L.t=1; L.dragD=-1; retarget(L,0,riffleDur()); L.dragging=!!forDrag;
    flights.unshift(L);
  }
  L.mesh.visible=true; updateLeafGeo(L);
  if(!forDrag){ if(silentTurns>0) silentTurns--; else flick(1); }
  afterTurnChange();
  return L;
}
function requestTurn(d,soft){
  if(lost||simpleMode||!openState||jumpLeaf||jumpPending!=null) return;
  if(d>0){ if(soft&&pendNext>=2) return; pendNext++; }
  else   { if(soft&&pendPrev>=2) return; pendPrev++; }
  pump();
}
function pump(){
  if(pendNext&&pendPrev){ var m=Math.min(pendNext,pendPrev); pendNext-=m; pendPrev-=m; }
  if(!pendNext&&!pendPrev) return;
  if(jumpLeaf||jumpPending!=null){ pendNext=pendPrev=0; return; }
  var now=performance.now(), wait=lastLaunch+stagger()-now;
  if(wait>4){ if(!pumpTimer) pumpTimer=setTimeout(function(){pumpTimer=0;pump();},wait+2); return; }
  if(flights.length>=MAXF) return;
  var d=pendNext?1:-1;
  if(d>0)pendNext--; else pendPrev--;
  lastLaunch=now;
  doTurn(d,false);
  if(pendNext||pendPrev){ if(!pumpTimer) pumpTimer=setTimeout(function(){pumpTimer=0;pump();},stagger()); }
}
function jumpTo(sp,forceJump){
  if(lost||simpleMode||!openState) return;
  sp=Math.max(0,Math.min(SPREADS-1,sp));
  pendNext=pendPrev=0;
  if(jumpLeaf||jumpPending!=null) return;
  var cur=destSpread();
  if(sp===cur) return;
  if(Math.abs(sp-cur)<=2&&flights.length===0&&!forceJump){
    var d=sp>cur?1:-1, n=Math.abs(sp-cur);
    flick(1); silentTurns+=n;          /* one action, one sound - not one per page */
    for(var i=0;i<n;i++) requestTurn(d);
    return;
  }
  if(flights.length){
    for(var j=0;j<flights.length;j++){ var L=flights[j];
      if(!L.done&&!L.dragging){ L.from=L.t; L.t0=performance.now(); L.dur=140; } }
    jumpPending=sp; updateCount(); ensureTick(); return;
  }
  launchJump(sp);
}
function launchJump(sp){
  jumpPending=null;
  if(sp===sBase){ onLand(); return; }
  var L=acquire(); L.isJump=true; L.fromS=sBase; L.destS=sp;
  var jDur=closing?520:TURN;                  /* closing the book is one brisk motion */
  if(sp>sBase){ L.front=V[2*sBase+1]; L.back=V[2*sp];   L.t=0; L.dragD=1;  retarget(L,1,jDur); }
  else        { L.front=V[2*sp+1];   L.back=V[2*sBase]; L.t=1; L.dragD=-1; retarget(L,0,jDur); }
  L.mesh.visible=true; updateLeafGeo(L); jumpLeaf=L;
  flick(1.1);
  afterTurnChange();
}

/* ---------------- UI events ---------------- */
function bindUI(){
  els.close.onclick=function(){ apiClose(false); };
  els.next.onclick=function(){ requestTurn(1); };
  els.prev.onclick=function(){ requestTurn(-1); };

  /* which side of the SPINE was hit (not the canvas midline - they differ
     when a single page is centered) */
  function dirAt(x){ return (x>=viewMetrics().spineX)?1:-1; }
  function abortLeaf(L){
    retarget(L, L.dragD>0?0:1, 240); ensureTick();
  }
  function cancelCurlDrag(){
    if(!dragging||!dragL) return;
    var L=dragL; dragL=null; dragging=false; L.dragging=false;
    els.cv.classList.remove('grabbing');
    abortLeaf(L);
  }
  function pcount(){ return Object.keys(ptrs).length; }

  els.cv.addEventListener('pointerdown',function(e){
    if(lost||simpleMode||!glReady) return;
    root.classList.add('touched');
    ptrs[e.pointerId]={x:e.clientX,y:e.clientY};
    try{ els.cv.setPointerCapture(e.pointerId); }catch(err){}
    if(pcount()===2){                       /* second finger = pinch zoom */
      cancelCurlDrag(); panDrag=null; touchPend=null;
      var ks=Object.keys(ptrs), a=ptrs[ks[0]], b=ptrs[ks[1]];
      pinch0={d:Math.max(24,Math.hypot(a.x-b.x,a.y-b.y)), z:zoom};
      return;
    }
    if(zoom>1){                             /* zoomed: drag pans the page */
      panDrag={x:e.clientX,y:e.clientY,px:panX,py:panY};
      els.cv.classList.add('grabbing');
      return;
    }
    if(e.pointerType!=='mouse'){
      /* touch: the SWIPE DIRECTION picks the page (right-to-left = next,
         left-to-right = PREVIOUS - the native single-page gesture); the leaf
         launches on the first horizontal movement, a quick tap still turns by
         side. Mouse keeps the approved instant lift + spam-riffle. */
      touchPend={x:e.clientX,y:e.clientY,t:performance.now()};
      els.cv.classList.add('grabbing');
      return;
    }
    var d=dirAt(e.clientX);
    var now=performance.now();
    if(now-lastLaunch<RIFFLE_MS*0.7){ requestTurn(d); return; }
    lastLaunch=now;
    var res=doTurn(d,true);
    if(res==='wait'){ requestTurn(d); return; }
    if(res===null||res==='flip') return;
    dragL=res; dragging=true; moved=0; startX=e.clientX; downT=now;
    els.cv.classList.add('grabbing');
  });
  els.cv.addEventListener('pointermove',function(e){
    if(ptrs[e.pointerId]) ptrs[e.pointerId]={x:e.clientX,y:e.clientY};
    if(pinch0&&pcount()>=2){
      var ks=Object.keys(ptrs), a=ptrs[ks[0]], b=ptrs[ks[1]];
      setZoom(pinch0.z*Math.hypot(a.x-b.x,a.y-b.y)/pinch0.d,(a.x+b.x)/2,(a.y+b.y)/2);
      return;
    }
    if(panDrag){
      var m=viewMetrics();
      panX=panDrag.px-((e.clientX-panDrag.x)/m.w)*(m.visW/zoom);
      panY=panDrag.py+((e.clientY-panDrag.y)/m.h)*(m.visH/zoom);
      clampPan(); applyViewX(); requestRender();
      return;
    }
    if(touchPend&&!dragging){
      /* first decisive horizontal movement launches the leaf in the SWIPED
         direction and hands over to the normal drag-to-curl below */
      var dx=e.clientX-touchPend.x, dy=e.clientY-touchPend.y;
      if(Math.abs(dx)<8||Math.abs(dx)<=Math.abs(dy)) return;
      var td=dx<0?1:-1;
      lastLaunch=performance.now();
      var tres=doTurn(td,true);
      if(tres==='wait'||tres===null||tres==='flip'){
        if(tres==='wait') requestTurn(td);
        touchPend=null; return;
      }
      dragL=tres; dragging=true; moved=Math.abs(dx); startX=touchPend.x; downT=touchPend.t;
      touchPend=null;
    }
    if(!dragging||!dragL) return;
    var range=viewMetrics().pw;
    moved=Math.max(moved,Math.abs(e.clientX-startX));
    var raw=(dragL.dragD>0)?(startX-e.clientX)/range:1-((e.clientX-startX)/range);
    var di=flights.indexOf(dragL);
    if(di>0) raw=Math.min(raw,flights[di-1].t-GAP_T);                 /* can't push through the sheet ahead */
    if(di>=0&&di<flights.length-1) raw=Math.max(raw,flights[di+1].t+GAP_T);
    dragL.t=Math.max(0,Math.min(1,raw)); dragL.done=false;
    dragL.kEff=null;
    updateLeafGeo(dragL); applyViewX(); requestRender();
  });
  function endPointer(e){
    if(e&&ptrs[e.pointerId]) delete ptrs[e.pointerId];
    if(pcount()<2) pinch0=null;
    if(panDrag&&pcount()===0){ panDrag=null; els.cv.classList.remove('grabbing'); }
    if(touchPend){
      /* lifted without horizontal movement = a tap = turn by side (queued,
         so spam-taps riffle exactly like clicks) */
      var wasTap=(performance.now()-touchPend.t)<350, tapX=touchPend.x;
      touchPend=null;
      els.cv.classList.remove('grabbing');
      if(wasTap&&e&&e.type!=='pointercancel') requestTurn(dirAt(tapX));
      return;
    }
    if(!dragging||!dragL) return;
    dragging=false; els.cv.classList.remove('grabbing');
    var L=dragL; dragL=null; L.dragging=false;
    /* a click is a click even if the mouse slid a little or the page barely lifted */
    var quick=(performance.now()-downT)<350;
    var progress=(L.dragD>0)?L.t:(1-L.t);
    if(moved<10||(quick&&progress<0.05)){
      retarget(L, L.dragD>0?1:0, riffleDur()); flick(0.9); ensureTick();
      return;
    }
    var goal=(L.dragD>0)?(L.t>0.35?1:0):(L.t<0.65?0:1);
    if(L.dragD>0&&goal===0&&flights[flights.length-1]!==L) goal=1;
    if(L.dragD<0&&goal===1&&flights[0]!==L) goal=0;
    if(goal===(L.dragD>0?1:0)) flick(0.9);
    retarget(L,goal,TURN*0.75); ensureTick();
  }
  els.cv.addEventListener('pointerup',endPointer);
  els.cv.addEventListener('pointercancel',endPointer);

  /* double-click only EXITS zoom (while zoomed, clicks pan, so nothing collides);
     zooming IN is the toolbar buttons / pinch / ctrl+wheel / +,- keys -
     dblclick-to-zoom-in stole quick two-page flicks and was removed */
  els.cv.addEventListener('dblclick',function(e){
    if(lost||simpleMode||!glReady) return;
    if(zoom>1){ e.preventDefault(); setZoom(1); }
  });

  /* wheel = zoom toward the cursor - the gesture everyone tries when the text
     is small, and the flipbook-viewer standard. Trackpad pinch arrives as
     ctrl+wheel and lands on the same path. Page turns live on clicks, arrows,
     drag and the scrubber. */
  els.stage.addEventListener('wheel',function(e){
    if(simpleMode||!openState||closing) return;
    e.preventDefault();
    setZoom(zoom*Math.pow(1.0022,-e.deltaY),e.clientX,e.clientY);
  },{passive:false});

  /* scrubber */
  function setScrubFromEvent(e){
    var r=els.scrub.querySelector('.tr').getBoundingClientRect();
    var p=Math.max(0,Math.min(1,(e.clientX-r.left)/r.width));
    var sp=Math.round(p*(SPREADS-1));
    lastScrub=sp;
    var pv=SPREADS>1?sp/(SPREADS-1):0;
    setScrub(pv);
    els.sbub.style.left=(pv*100)+'%';
    var src=V[2*sp+1]||V[2*sp];
    if(src&&els.simg.getAttribute('src')!==src) els.simg.src=src;
    els.slabel.textContent='Page '+pagesLabel(sp);
  }
  els.scrub.addEventListener('pointerdown',function(e){
    if(simpleMode) return;
    scrubbing=true; els.scrub.classList.add('live'); els.scrub.setPointerCapture(e.pointerId); setScrubFromEvent(e);
  });
  els.scrub.addEventListener('pointermove',function(e){ if(scrubbing) setScrubFromEvent(e); });
  function scrubEnd(){
    if(!scrubbing) return;
    scrubbing=false; els.scrub.classList.remove('live');
    jumpTo(lastScrub); updateCount();
  }
  els.scrub.addEventListener('pointerup',scrubEnd);
  els.scrub.addEventListener('pointercancel',scrubEnd);

  /* all-pages grid */
  els.gridBtn.onclick=function(){ if(gridOpen()) closeGrid(); else openGrid(); };
  els.gclose.onclick=closeGrid;

  /* zoom buttons */
  els.zinBtn.onclick=function(){ setZoom(zoom*1.35); };
  els.zoutBtn.onclick=function(){ setZoom(zoom/1.35); };
  els.zchip.onclick=function(){ setZoom(1); };

  /* page-turn sound */
  els.sndBtn.onclick=function(){
    soundOn=!soundOn;
    try{ localStorage.setItem('atbrr-snd',soundOn?'1':'0'); }catch(e){}
    this.innerHTML=soundOn?ICONS.snd:ICONS.sndOff;
    if(soundOn) flick(1);
  };

  /* scroll view toggle */
  els.scrollBtn.onclick=function(){
    if(lost){ return; }                       /* context lost pins us to scroll view */
    if(simpleMode) showGL(); else showFallback('');
  };

  /* fullscreen */
  els.fsBtn.onclick=function(){
    if(document.fullscreenElement) document.exitFullscreen();
    else if(root.requestFullscreen) root.requestFullscreen();
  };
  document.addEventListener('fullscreenchange',function(){ setTimeout(resize,60); });

  /* page-number input */
  els.count.onclick=function(){
    if(countEditing||simpleMode) return;
    countEditing=true; els.count.innerHTML='';
    var inp=document.createElement('input'); inp.type='number'; inp.min=1; inp.max=N; inp.placeholder='1-'+N;
    els.count.appendChild(inp); inp.focus();
    function done(commit){
      var v=parseInt(inp.value,10);
      countEditing=false;
      if(commit&&v>=1&&v<=N) jumpTo(spreadOfPage(v)); else updateCount();
    }
    inp.onkeydown=function(ev){ ev.stopPropagation(); if(ev.key==='Enter')done(true); if(ev.key==='Escape')done(false); };
    inp.onblur=function(){ if(countEditing) done(false); };
  };

  /* keyboard - bound once, guarded by openState */
  document.addEventListener('keydown',function(e){
    if(!openState) return;
    if(e.target&&(e.target.tagName==='INPUT'||e.target.tagName==='TEXTAREA')) return;
    if(closing){ if(e.key==='Escape') apiClose(false); return; }   /* second Esc skips the animation */
    if(root.classList.contains('opening')){ if(e.key==='Escape') apiClose(false); return; }
    if(e.key==='Escape'){
      if(gridOpen()) closeGrid();
      else if(zoom>1) setZoom(1);
      else if(!document.fullscreenElement) apiClose(false);
      return;
    }
    if(gridOpen()) return;                  /* the grid owns the keys except Escape */
    if(e.key==='ArrowRight'||e.key==='PageDown'){ requestTurn(1,e.repeat); e.preventDefault(); }
    else if(e.key==='ArrowLeft'||e.key==='PageUp'){ requestTurn(-1,e.repeat); e.preventDefault(); }
    else if(e.key==='Home'){ jumpTo(0); e.preventDefault(); }
    else if(e.key==='End'){ jumpTo(SPREADS-1); e.preventDefault(); }
    else if(e.key==='+'||e.key==='='){ setZoom(zoom*1.35); e.preventDefault(); }
    else if(e.key==='-'){ setZoom(zoom/1.35); e.preventDefault(); }
    else if(e.key==='0'){ setZoom(1); e.preventDefault(); }
  });

  /* browser back closes the reader */
  window.addEventListener('popstate',function(){
    if(openState&&!/read=/.test(location.hash)) apiClose(true);
  });

  window.addEventListener('resize',function(){
    if(!openState) return;
    syncViewMode();                    /* portrait-phone boundary crossings remap the book */
    resize();
  });
  /* the stage can change size WITHOUT a window resize (the bottom bar re-wraps
     when the sound button appears, iOS URL bar collapses) - a stale camera then
     distorts the book and shifts the FLIP landing */
  if(window.ResizeObserver){
    new ResizeObserver(function(){ if(openState) resize(); }).observe(els.stage);
  }
}

function setScrub(p){ var pct=(p*100)+'%'; els.sfill.style.width=pct; els.shandle.style.left=pct; }

/* ---------------- all-pages grid overview ---------------- */
function buildGrid(){
  if(gridBuilt) return; gridBuilt=true;
  els.gwrap.innerHTML='';
  /* PORT NOTE: feed a small thumb tier here, not the full 1024px pages */
  for(var p2=1;p2<=N;p2++){
    var b=document.createElement('button'); b.type='button'; b.className='brr-gth';
    b.innerHTML='<img loading="lazy" src="'+P[p2-1]+'" alt=""><span>Page '+p2+'</span>';
    (function(pp){ b.onclick=function(){ closeGrid(); jumpTo(spreadOfPage(pp)); }; })(p2);
    els.gwrap.appendChild(b);
  }
}
function gridOpen(){ return !!(els.grid&&els.grid.classList.contains('on')); }
function openGrid(){
  buildGrid();
  els.gtitle.textContent=REC?REC.title:'';
  highlightGrid(destSpread());
  els.grid.classList.add('on');
  els.gridBtn.setAttribute('aria-pressed','true');
  var cur=els.gwrap.querySelector('.cur');
  if(cur&&cur.scrollIntoView) cur.scrollIntoView({block:'center'});
}
function closeGrid(){
  els.grid.classList.remove('on');
  els.gridBtn.setAttribute('aria-pressed','false');
}
function highlightGrid(dsp){
  if(!gridBuilt) return;
  var kids=els.gwrap.children;
  for(var i=0;i<kids.length;i++) kids[i].classList.toggle('cur',spreadOfPage(i+1)===dsp);
}

/* ---------------- sizing + view centering + cover FLIP ---------------- */
/* screen-space metrics of the book - pure math, works before three loads */
function viewMetrics(){
  var r=els.cv.getBoundingClientRect(), w=r.width||1, h=r.height||1;
  var vF=30*Math.PI/180, aspect=w/h;
  var dH=(H/2+MARGIN)/Math.tan(vF/2);
  /* fit target: the full spread (2W wide -> half-width W), or ONE page in
     single mode (half-width W/2) - that is what makes phone pages big */
  var hF=2*Math.atan(Math.tan(vF/2)*aspect), dW=((single?W/2:W)+MARGIN)/Math.tan(hF/2);
  var d=Math.max(dH,dW), visH=2*d*Math.tan(vF/2), visW=visH*aspect;
  return {rect:r,w:w,h:h,dist:d,visW:visW,visH:visH,
          pw:W/visW*w, ph:H/visH*h,
          spineX:r.left + w/2 - (viewX/visW)*w};
}
/* where should the camera sit for a spread? single visible page = centered */
function centerXOf(sp){
  sp=Math.max(0,Math.min(SPREADS-1,sp));
  var hasL=!!V[2*sp], hasR=!!V[2*sp+1];
  if(hasR&&!hasL) return W/2;
  if(hasL&&!hasR) return -W/2;
  return 0;
}
/* camera x driven by the PAGES, not the destination: the continuous spread
   position = sBase + each airborne sheet's progress, so the re-centering slide
   happens exactly as the cover physically opens (and reverses if it's caught).
   Continuous across every launch/commit/reversal - no separate clock, no jump. */
function currentViewX(){
  var s;
  if(jumpLeaf){
    var p=(jumpLeaf.destS>jumpLeaf.fromS)?jumpLeaf.t:(1-jumpLeaf.t);
    s=jumpLeaf.fromS+p*(jumpLeaf.destS-jumpLeaf.fromS);
  } else {
    s=sBase;
    for(var i=0;i<flights.length;i++) s+=flights[i].t;
  }
  var lo=Math.floor(s), f=s-lo;
  return centerXOf(lo)*(1-f)+centerXOf(lo+1)*f;
}
function applyViewX(){
  if(!glReady) return;
  viewX=currentViewX();
  camera.position.x=viewX+panX;
  camera.position.y=panY;
}
function resize(){
  if(!glReady||!openState) return;
  var w=els.cv.clientWidth,h=els.cv.clientHeight; if(!w||!h) return;
  renderer.setSize(w,h,false); camera.aspect=w/h;
  var vF=camera.fov*Math.PI/180, dH=(H/2+MARGIN)/Math.tan(vF/2);
  /* keep in lockstep with viewMetrics(): single mode frames ONE page */
  var hF=2*Math.atan(Math.tan(vF/2)*camera.aspect), dW=((single?W/2:W)+MARGIN)/Math.tan(hF/2);
  var dist=Math.max(dH,dW);
  /* tight near/far = the depth precision that stops flat sheets z-fighting */
  camera.near=Math.max(0.5,dist-2.4); camera.far=dist+2.4;
  camera.zoom=zoom; clampPan();
  camera.position.set(viewX+panX,panY,dist); camera.updateProjectionMatrix();
  requestRender();
}
/* where the cover page will sit on screen, in px */
function coverRectPx(){
  var m=viewMetrics();
  if(!m.w||!m.h) return null;
  var left=m.rect.left + m.w/2 + ((0-viewX)/m.visW)*m.w;
  return {left:left, top:m.rect.top+(m.h-m.ph)/2, width:m.pw, height:m.ph};
}

/* ---------------- reading zoom + pan ---------------- */
function clampPan(){
  if(zoom<=1){ panX=0; panY=0; return; }   /* no pan at fit - the base view owns the camera */
  var m=viewMetrics(), mg=0.12;
  var halfW=m.visW/(2*zoom), halfH=m.visH/(2*zoom);
  /* content spans the spread (-W..W); in single mode only the right page (0..W) */
  var cl=single?0:-W;
  var lo=cl-mg+halfW, hi=(W+mg)-halfW;
  var cx=viewX+panX;
  /* if the view is still wider than the spread, hold the BASE position
     (cover stays cover-centered), never snap to the spine */
  cx=(lo>hi)?viewX:Math.max(lo,Math.min(hi,cx));
  panX=cx-viewX;
  var loY=-(H/2+mg)+halfH, hiY=(H/2+mg)-halfH;
  panY=(loY>hiY)?0:Math.max(loY,Math.min(hiY,panY));
}
function setZoom(z,cx,cy){
  z=Math.max(1,Math.min(4,z));
  var m=viewMetrics();
  if(cx!=null&&m.w){
    /* keep the world point under the cursor fixed while zooming */
    var relX=(cx-m.rect.left-m.w/2)/m.w, relY=(cy-m.rect.top-m.h/2)/m.h;
    var wx=viewX+panX+relX*(m.visW/zoom), wy=panY-relY*(m.visH/zoom);
    zoom=z;
    panX=wx-relX*(m.visW/zoom)-viewX; panY=wy+relY*(m.visH/zoom);
  } else zoom=z;
  if(zoom===1){ panX=0; panY=0; }
  clampPan();
  if(glReady){ camera.zoom=zoom; camera.updateProjectionMatrix(); applyViewX(); }
  root.classList.toggle('zoomed',zoom>1);
  if(els.zchip){
    els.zchip.textContent=Math.round(zoom*100)+'%';
    els.zchip.classList.toggle('on',zoom>1);
  }
  requestRender();
}

/* ---------------- paper flick (recorded sample only) ---------------- */
function loadSound(){
  if(sndTried||!SND_SRC) return; sndTried=true;
  try{
    AC=AC||new (window.AudioContext||window.webkitAudioContext)();
    fetch(SND_SRC)
      .then(function(r){ if(!r.ok) throw 0; return r.arrayBuffer(); })
      .then(function(ab){ return AC.decodeAudioData(ab); })
      .then(function(buf){ sndBuf=buf; if(els.sndBtn) els.sndBtn.style.display=''; })
      .catch(function(){ sndBuf=null; if(els.sndBtn) els.sndBtn.style.display='none'; });
  }catch(e){ if(els.sndBtn) els.sndBtn.style.display='none'; }
}
function flick(v){
  if(!soundOn||!openState||!sndBuf) return;
  try{
    if(AC.state==='suspended') AC.resume();
    var src=AC.createBufferSource(); src.buffer=sndBuf;
    src.playbackRate.value=0.92+Math.random()*0.16;   /* slight variance so riffles don't machine-gun */
    var g=AC.createGain(); g.gain.value=0.5*(v||1);
    src.connect(g); g.connect(AC.destination); src.start();
  }catch(e){}
}

/* ---------------- shareable position: #read=<id>&p=<page> ---------------- */
function syncHash(){
  if(!openState||closing||!REC||!/read=/.test(location.hash||'')) return;
  var p=pageOfSpread(destSpread());
  try{ history.replaceState(history.state,'','#read='+REC.id+(p>1?'&p='+p:'')); }catch(e){}
}
function flipFrom(coverEl){
  if(!REC||!REC.cover||!coverEl) return;
  var r0=coverEl.getBoundingClientRect();
  if(!r0.width) return;
  var img=new Image(); img.src=REC.cover; img.className='brr-flip'; img.alt='';
  img.style.left=r0.left+'px'; img.style.top=r0.top+'px';
  img.style.width=r0.width+'px'; img.style.height=r0.height+'px';
  document.body.appendChild(img);
  flipImg=img; flipT0=performance.now();
  coverHold=true;                    /* the real cover stays hidden while the clone flies */
  root.classList.add('opening');     /* no page input until the entrance hands over */
  requestAnimationFrame(function(){
    var tr=coverRectPx();
    if(!tr){ dropFlip(true); return; }
    img.style.transition='left .5s cubic-bezier(.22,.61,.36,1), top .5s cubic-bezier(.22,.61,.36,1), width .5s cubic-bezier(.22,.61,.36,1), height .5s cubic-bezier(.22,.61,.36,1), border-radius .5s ease';
    img.style.left=tr.left+'px'; img.style.top=tr.top+'px';
    img.style.width=tr.width+'px'; img.style.height=tr.height+'px';
    img.style.borderRadius='2px';
  });
  /* safety: never let the clone outlive its welcome */
  setTimeout(function(){ dropFlip(); },1400);
}
function revealCover(){
  if(root) root.classList.remove('opening');   /* entrance over = input returns */
  if(!coverHold) return;
  coverHold=false;
  if(glReady&&openState){ refreshMaps(); render(); }
}
function dropFlip(fast){
  if(!flipImg){ if(fast) revealCover(); return; }
  var img=flipImg; flipImg=null;
  var wait=fast?0:Math.max(0,520-(performance.now()-flipT0));
  setTimeout(function(){
    /* layout can drift between measure and landing (scrollbar, toolbar
       buttons appearing) - re-measure and heal before the handoff */
    if(!fast&&openState){
      var tr=coverRectPx();
      if(tr){
        img.style.transition='left .12s ease-out, top .12s ease-out, width .12s ease-out, height .12s ease-out';
        img.style.left=tr.left+'px'; img.style.top=tr.top+'px';
        img.style.width=tr.width+'px'; img.style.height=tr.height+'px';
      }
    }
    setTimeout(function(){
      revealCover();                 /* canvas cover appears exactly as the clone fades */
      img.style.transition='opacity .22s ease'; img.style.opacity='0';
      setTimeout(function(){ if(img.parentNode) img.parentNode.removeChild(img); },240);
    },fast?0:130);
  },wait);
}

/* ---------------- single-page portrait mode ---------------- */
/* On a narrow portrait viewport a two-page spread renders ~180px-wide pages -
   unreadable. Single mode gives every "spread" ONE page: V=[null,p1,null,p2,...]
   so V[2s]=null, V[2s+1]=p(s+1). The existing machinery does the rest:
   centerXOf/currentViewX center a lone right page, resize() fits ONE page
   width (pages render ~2x larger), and a turning sheet's back (V[2k+2]=null)
   falls back to BLANK paper - an honest loose leaf; the next page is revealed
   BENEATH it because the static stack already tracks the destination spread.
   Only page-number <-> spread mapping needs mode-aware branches. */
function wantSingle(){ return innerWidth<768 && innerHeight>innerWidth; }
function buildV(){
  if(single){
    V=[];
    for(var i=0;i<N;i++){ V.push(null); V.push(P[i]); }
  } else {
    V=[null].concat(P); if(V.length%2) V.push(null);
  }
  SPREADS=V.length/2;
}
function spreadOfPage(p){ return single?(p-1):Math.floor(p/2); }
function pageOfSpread(s){ return single?(s+1):(V[2*s]?2*s:2*s+1); }
/* rotating/resizing across the portrait-phone boundary rebuilds the book
   around the SAME page: settle flights, remap V, keep reading */
function syncViewMode(){
  if(!REC||closing) return;
  var want=wantSingle();
  if(want===single) return;
  sBase=Math.max(0,Math.min(SPREADS-1,destSpread()));
  clearFlights();
  var page=Math.max(1,Math.min(N,pageOfSpread(sBase)));
  single=want;
  buildV();
  sBase=Math.max(0,Math.min(SPREADS-1,spreadOfPage(page)));
  viewX=centerXOf(sBase);
  setZoom(1);
  untuck();
  tucked=single&&SPREADS>1&&sBase===SPREADS-1;
  if(glReady){ syncTextures(); refreshMaps(); updateCount(); requestRender(); }
}

/* ---------------- load a brochure into the reader ---------------- */
function loadRec(rec){
  gen++;
  REC=rec;
  setAspect(rec.ar);
  N=rec.pages||1;
  P=[]; for(var i=1;i<=N;i++) P.push(pageUrl(rec,i));
  single=wantSingle();
  buildV();
  sBase=0; clearFlights();
  if(startPageReq){                    /* deep link straight to a page */
    sBase=Math.max(0,Math.min(SPREADS-1,spreadOfPage(startPageReq)));
    startPageReq=0;
  }
  untuck();
  tucked=single&&SPREADS>1&&sBase===SPREADS-1;   /* opened AT the back cover = already closed */
  viewX=centerXOf(sBase);              /* open centered, no slide */
  setZoom(1);
  Object.keys(CACHE).forEach(function(k){ CACHE[k].dispose(); delete CACHE[k]; });
  pendingTex={};
  gridBuilt=false; els.gwrap.innerHTML=''; closeGrid();
  countEditing=false; scrubbing=false;
  /* header */
  var bn=rec.brandName||'';
  if(!bn&&window.BRANDS){ var b=window.BRANDS.filter(function(x){return x.id===rec.brand;})[0]; if(b) bn=b.name; }
  els.title.textContent=rec.title||'';
  var meta=[bn,(rec.pages?rec.pages+' pages':''), rec.year||''].filter(Boolean);
  els.meta.textContent=meta.join(' · ');
  /* scroll view content */
  els.scroll.innerHTML='';
  P.forEach(function(src){ var im=new Image(); im.src=src; im.alt=''; im.loading='lazy'; els.scroll.appendChild(im); });
  buildBrandBlank(rec);
  veil(true);
  if(glReady){ syncTextures(); refreshMaps(); updateCount(); resize(); requestRender(); maybeReady(); }
  runTestModes();
}

/* ---------------- open / close ---------------- */
function apiOpen(rec,opts){
  opts=opts||{};
  if(!rec||!rec.pages) return;
  /* self-heal: a stuck flag with the overlay actually hidden must never
     eat clicks forever - reset and open */
  if((closing||openState)&&root&&root.hidden){ closing=false; openState=false; }
  if(closing) return;
  if(opts.brandName&&!rec.brandName) rec.brandName=opts.brandName;
  injectCSS(); buildDOM();
  if(openState&&REC&&REC.id===rec.id) return;
  openState=true;
  openerCover=opts.coverEl||null;
  loadSound();
  if(opts.pageUrl) pageUrl=opts.pageUrl; else pageUrl=defaultPageUrl;
  var pm=(location.hash||'').match(/[#&]p=(\d+)/);
  if(opts.page) startPageReq=+opts.page;
  else if(opts.fromHash&&pm) startPageReq=+pm[1];
  setAspect(rec.ar);                /* before any coverRectPx() measurement */
  viewX=startPageReq?0:W/2;         /* cover = centered single page */
  root.hidden=false;
  requestAnimationFrame(function(){ root.classList.add('on'); });
  /* lock scroll WITHOUT the layout shift: removing the scrollbar widens the
     viewport ~15px and everything (hub card + canvas) re-centers mid-flight -
     compensate with padding (the lightbox lesson) */
  var sbw=window.innerWidth-document.documentElement.clientWidth;
  document.documentElement.style.overflow='hidden';
  document.body.style.overflow='hidden';
  if(sbw>0) document.body.style.paddingRight=sbw+'px';
  if(!opts.fromHash){
    try{ history.pushState({atbrr:rec.id},'','#read='+rec.id); }catch(e){}
  }
  REC=rec;
  if(opts.coverEl) flipFrom(opts.coverEl);
  ensureThree(function(){
    if(!openState) return;
    if(initGL()) { simpleMode=false; root.classList.remove('simple'); els.scrollBtn.setAttribute('aria-pressed','false'); }
    loadRec(rec);
    if(!glReady) showFallback('');
  },function(msg){
    if(!openState) return;
    loadRec(rec); showFallback(msg);
  });
}
/* where the cover WILL sit once the book has ridden home (viewX = W/2) */
function coverRectFinal(){
  var m=viewMetrics();
  if(!m.w||!m.h) return null;
  var left=m.rect.left + m.w/2 + ((0-W/2)/m.visW)*m.w;
  return {left:left, top:m.rect.top+(m.h-m.ph)/2, width:m.pw, height:m.ph};
}
/* close = one continuous motion: the LIVE canvas flies down onto the card
   (a single composited transform) WHILE the pages ride back to the cover
   inside it, chrome and backdrop melting away around the flight. The ride is
   a short riffle nearby, ONE jump-turn from deep in the book - constant cost
   on any page count. Nothing reflows (scrollbar is compensated). */
function apiClose(fromPop){
  if(!openState) return;
  if(closing){                                /* second Esc/X = skip the theatrics */
    if(closeTimer){ clearTimeout(closeTimer); closeTimer=0; }
    closing=false; finishClose(fromPop); return;
  }
  var reduced=false;
  try{ reduced=matchMedia('(prefers-reduced-motion: reduce)').matches; }catch(e){}
  var canFly=!lost&&glReady&&!simpleMode&&!reduced
             &&openerCover&&document.contains(openerCover);
  var F=canFly?coverRectFinal():null, C=null;
  if(canFly){ try{ C=openerCover.getBoundingClientRect(); }catch(e){} }
  if(!F||!C||!C.width){ finishClose(fromPop); return; }
  closing=true;
  root.classList.add('closing');
  dropFlip(true);                             /* closing mid-entrance ends the zoom first */
  closeGrid(); setZoom(1);
  pendNext=pendPrev=0;
  /* you don't leave a book page by page - the whole read block flips shut
     as ONE motion (the returning sheet's face IS the cover) */
  if(destSpread()>0) jumpTo(0,true);
  var r=els.cv.getBoundingClientRect();
  var s=C.width/F.width;
  var dx=(C.left+C.width/2)-(F.left+F.width/2), dy=(C.top+C.height/2)-(F.top+F.height/2);
  var dur=760;
  els.cv.style.transformOrigin=(F.left+F.width/2-r.left)+'px '+(F.top+F.height/2-r.top)+'px';
  root.classList.add('flying');
  requestAnimationFrame(function(){
    els.cv.style.transition='transform '+dur+'ms cubic-bezier(.45,.08,.3,1)';
    els.cv.style.transform='translate('+dx+'px,'+dy+'px) scale('+s+')';
  });
  closeTimer=setTimeout(function(){
    closeTimer=0;
    root.classList.remove('on');              /* the landed canvas fades into the real card */
    setTimeout(function(){ closing=false; finishClose(fromPop); },200);
  },dur);
}
function finishClose(fromPop){
  if(!openState) return;
  openState=false; closing=false;
  if(closeTimer){ clearTimeout(closeTimer); closeTimer=0; }
  root.classList.remove('on'); root.classList.remove('closing');
  coverHold=false; dropFlip(true);
  setZoom(1); closeGrid(); untuck();
  ptrs={}; pinch0=null; panDrag=null; touchPend=null; openerCover=null;
  if(document.fullscreenElement&&document.exitFullscreen) document.exitFullscreen();
  setTimeout(function(){
    if(openState) return;                       /* reopened during the fade */
    root.hidden=true;
    root.classList.remove('flying'); root.classList.remove('opening');
    els.cv.style.transform=''; els.cv.style.transition=''; els.cv.style.transformOrigin='';
    sBase=Math.max(0,Math.min(SPREADS-1,destSpread()));   /* settle any unfinished ride */
    clearFlights();
    Object.keys(CACHE).forEach(function(k){ CACHE[k].dispose(); delete CACHE[k]; });
    if(BRAND_BLANK){ BRAND_BLANK.dispose(); BRAND_BLANK=null; }
    pendingTex={};
    if(tickTimer){ clearTimeout(tickTimer); tickTimer=0; }
    if(tickId){ cancelAnimationFrame(tickId); tickId=0; }
    setDrawing(false);
  },300);
  document.documentElement.style.overflow='';
  document.body.style.overflow='';
  document.body.style.paddingRight='';
  if(!fromPop&&history.state&&history.state.atbrr){
    try{ history.back(); }catch(e){}
  }
}

/* ---------------- harness test modes ---------------- */
function runTestModes(){
  if(/[?&]selftest=1/.test(location.search)&&!selftestRan){
    selftestRan=true;
    var OUT={steps:[],pass:true}, maxFl=0;
    setInterval(function(){ maxFl=Math.max(maxFl,flights.length); },16);
    var check=function(name,cond,info){ OUT.steps.push({name:name,ok:!!cond,info:info}); if(!cond)OUT.pass=false; };
    var settle=function(cb){ var stable=0;
      (function poll(){
        document.title='ST '+JSON.stringify(window.__diag?window.__diag():{});
        if(!flights.length&&!jumpLeaf&&jumpPending==null&&!pendNext&&!pendPrev){ if(++stable>=3) return cb(); }
        else stable=0;
        setTimeout(poll,80);
      })(); };
    var finish=function(){
      OUT.diag=window.__diag?window.__diag():{};
      var pre=document.createElement('pre'); pre.id='testout'; pre.textContent=JSON.stringify(OUT); document.body.appendChild(pre);
      document.title=OUT.pass?'SELFTEST-PASS':'SELFTEST-FAIL';
    };
    setTimeout(function(){
      if(simpleMode||!glReady){ check('webgl',false,'fallback mode'); return finish(); }
      var LAST=SPREADS-1;
      for(var i=0;i<LAST;i++) requestTurn(1);
      settle(function(){
        check('riffle-lands-on-end', sBase===LAST, sBase);
        check('riffle-overlapped', maxFl>=2, maxFl);
        for(var i=0;i<3;i++) requestTurn(-1);
        setTimeout(function(){ requestTurn(1); },180);
        settle(function(){
          check('mixed-nets-out', sBase===LAST-2, sBase);
          jumpTo(0);
          settle(function(){
            check('jump-home', sBase===0, sBase);
            for(var i=0;i<3;i++) requestTurn(1);
            setTimeout(function(){ jumpTo(LAST); },150);
            settle(function(){
              check('jump-mid-riffle', sBase===LAST, sBase);
              check('textures-bounded', residentCount()<=14, residentCount());
              finish();
            });
          });
        });
      });
    },600);
  }
  if(/[?&]rects=1/.test(location.search)){
    setTimeout(function(){
      function rc(el){ var r=el.getBoundingClientRect(); return [Math.round(r.left),Math.round(r.top),Math.round(r.width),Math.round(r.height)]; }
      var wide=[];
      document.querySelectorAll('body *').forEach(function(el){
        var r=el.getBoundingClientRect();
        if(r.width>innerWidth-14) wide.push((el.tagName+'.'+(el.className&&el.className.baseVal!==undefined?'svg':String(el.className).split(' ')[0])).slice(0,26)+'='+Math.round(r.width));
      });
      var st={vw:innerWidth,vh:innerHeight,sw:document.documentElement.scrollWidth,cv:rc(els.cv),wide:wide.slice(0,8)};
      if(glReady){
        /* ground truth: project the cover plane's corners through the REAL camera
           and compare against coverRectPx()'s math */
        var r=els.cv.getBoundingClientRect();
        var pr=function(x,y){ var v=new THREE.Vector3(x,y,-0.012); v.project(camera);
          return [r.left+(v.x+1)/2*r.width, r.top+(1-v.y)/2*r.height]; };
        var tl=pr(0,H/2), brc=pr(W,-H/2);
        st.plane=[Math.round(tl[0]),Math.round(tl[1]),Math.round(brc[0]-tl[0]),Math.round(brc[1]-tl[1])];
        var cr=coverRectPx();
        st.cover=[Math.round(cr.left),Math.round(cr.top),Math.round(cr.width),Math.round(cr.height)];
      }
      document.title='RECTS '+JSON.stringify(st);
    },1200);
  }
  if(/[?&]closetest=1/.test(location.search)&&glReady&&!window.__closetestRan){
    window.__closetestRan=true;
    setTimeout(function(){
      var dummy=document.createElement('div');
      /* display:block is load-bearing: the theme hides empty styled divs
         (base.css div:empty) and a hidden opener card kills the close-flight */
      dummy.style.cssText='position:fixed;left:120px;top:520px;width:180px;height:255px;display:block;';
      document.body.appendChild(dummy);
      openerCover=dummy;
      jumpTo(3);
      var t0=performance.now(), phase='close', trace=[], lastSig='';
      (function poll(){
        var dg=window.__diag();
        var sig=phase+'|'+sBase+'|'+dg.dest+'|'+dg.leaves+'|'+(!!jumpLeaf)+'|'+SPREADS+'|'+openState;
        if(sig!==lastSig){ lastSig=sig; trace.push(Math.round(performance.now()-t0)+':'+sig); }
        document.title='CT '+trace.slice(-6).join(' ');
        if(phase==='close'){
          if(openState&&!closing&&sBase===3&&!anyActive()&&jumpPending==null){ apiClose(false); phase='closing'; }
        } else if(phase==='closing'){
          if(!openState&&root.hidden){
            if(sBase!==0){ document.title='CLOSETEST-FAIL-sBase'+sBase+' T:'+trace.join(' '); return; }
            /* second act: reopening must work (the "only once" bug) */
            phase='reopen';
            var rec2=(window.ATBrochures||[]).filter(function(x){return x.id==='gb-duofix4';})[0];
            AtelierBrochureReader.open(rec2||REC,{coverEl:dummy});
          }
        } else if(phase==='reopen'){
          if(openState&&!root.hidden&&!closing){ document.title='CLOSETEST-PASS'; return; }
        }
        if(performance.now()-t0>20000){ document.title='CLOSETEST-TIMEOUT '+document.title; return; }
        setTimeout(poll,100);
      })();
    },800);
  }
  var poseM=location.search.match(/[?&]pose=(\w+)/);
  if(poseM&&!poseRan){
    poseRan=true;
    setTimeout(function(){
      if(simpleMode||!glReady) return;
      if(poseM[1]==='end'){ jumpTo(SPREADS-1); return; }   /* back page: verifies end-centering */
      if(poseM[1]==='grid'){ openGrid(); return; }
      if(poseM[1]==='zoom'){ var r=els.cv.getBoundingClientRect(); setZoom(2.3,r.left+r.width*0.62,r.top+r.height*0.45); return; }
      if(poseM[1]==='cover'){                              /* cover half-open: verifies the slide tracks the page */
        var C=doTurn(1,false);
        if(C&&C.t!==undefined){ C.dragging=true; C.t=0.5; updateLeafGeo(C); applyViewX(); refreshMaps(); render(); }
        return;
      }
      doTurn(1,false); doTurn(1,false); doTurn(1,false);
      var ts=(poseM[1]==='press')?[0.9,0.62,0.35]:[0.82,0.5,0.18];   /* press = the old tip-crossing case */
      for(var i=0;i<flights.length;i++){ var L=flights[i]; L.dragging=true; L.t=ts[i]||0.5; updateLeafGeo(L); }
      refreshMaps(); render();
    },1400);
  }
}

/* ---------------- public API + hooks ---------------- */
window.AtelierBrochureReader={
  open:apiOpen,
  close:function(){ apiClose(false); },
  isOpen:function(){ return openState; },
  tune:function(o){
    o=o||{};
    if(o.curl!=null) curlMax=o.curl;
    if(o.focus!=null) curlP=o.focus;
    if(o.turn!=null) TURN=o.turn;
    if(o.stagger!=null) RIFFLE_MS=o.stagger;
    if(o.shade!=null){ shadeAmt=o.shade; POOL.forEach(function(L){ L.mat.uniforms.uShade.value=o.shade; }); }
    return {curl:curlMax,focus:curlP,turn:TURN,stagger:RIFFLE_MS,shade:shadeAmt};
  }
};
window.__diag=function(){
  return {gl:glReady&&!lost,resident:residentCount(),mb:+(residentCount()*PAGE_MB).toFixed(1),
    sBase:sBase,dest:destSpread(),spreads:SPREADS,leaves:flights.length+(jumpLeaf?1:0),
    pendNext:pendNext,pendPrev:pendPrev,drawing:drawing,simple:simpleMode,open:openState,
    single:single,tucked:tucked};
};
window.__reader={
  next:function(){requestTurn(1);}, prev:function(){requestTurn(-1);}, jumpTo:jumpTo, diag:window.__diag
};
})();
